import api from "./CashFlowApi";
import PowerBIApi from "@/modules/powerBI/services/PowerBIApi";

import CashFlowRiportGroup, { CashFlowRiportGroupDefaultProps } from "../models/CashFlowRiportGroup";
import CashFlowRiportGroupItem, { CashFlowRiportGroupItemsDefaultProps } from "../models/CashFlowRiportGroupItem";
import CashFlowRiportHead, { CashFlowRiportHeadDefaultProps } from "../models/CashFlowRiportHead";
import AccountingYear from "../models/AccountingYear";
import GeneralLedgerRiportRowItem from "../models/GeneralLedgerRiportRowItem";
import GeneralLedgerRiportRowTransactionAmount from "../models/GeneralLedgerRiportRowTransactionAmount";
import PivotGroupData from "../models/PivotGroupData";
import Report from "@/modules/powerBI/models/Report";
import UserFeaturePermission from "@/models/FeaturePermission";
import { GlobalStore } from "@/models/GlobalStore";

export class CashFlowStore {

  cashFlowRiportHead: CashFlowRiportHead = {...CashFlowRiportHeadDefaultProps};
  cashFlowRiportGroups: CashFlowRiportGroup[] = [];
  cashFlowRiportGroupItems: CashFlowRiportGroupItem[] = [];

  profitGroups: CashFlowRiportGroup[] = [];
  profitGroupItems: PivotGroupData[] = [];  
  nonProfitGroups: CashFlowRiportGroup[] = [];
  nonProfitGroupItems: PivotGroupData[] = [];
  openAmountGroup: CashFlowRiportGroup = {...CashFlowRiportGroupDefaultProps}
  openAmountGroupItem: CashFlowRiportGroupItem = {...CashFlowRiportGroupItemsDefaultProps}

  cashFlowRiportChain: CashFlowRiportHead[] = [];

  cashBalance: {
    planBalance: number | 0;
    nonProfitMonthTrueAmount: number | 0;
    trueBalance: number | 0;
    lockedMonthBalance: number | 0;
  }[] = Array(12).fill(null).map(() => ({
    planBalance: 0,
    nonProfitMonthTrueAmount: 0,
    trueBalance: 0,
    lockedMonthBalance: 0,
  }));

  accYears: AccountingYear[] = [];
  generalLedgerRiportRowItems: GeneralLedgerRiportRowItem[] = [];
  generalLedgerRiportRowsTransAmounts: GeneralLedgerRiportRowTransactionAmount[] = [];

  biReports: Report[] = [];
  
  userPermissions = {
    showMaskInfo: false,
    canOverrideOpenTrueAmount: false,
    canEdit: false,
  };
  loading = false;

  //---------- Riport ----------//

  async loadCashFlowRiport(cashFlowRiportId: number) { 
    this.cashFlowRiportHead = await api.getCashFlowRiport(cashFlowRiportId);
  }
  
  async createCashFlowRiport(generalLedgerRiportId: number, accountingYear: number, isEmpty?: boolean, previousCashFlowRiportId?: number,) { 
   this.cashFlowRiportHead.id = await api.createCashFlowRiport(generalLedgerRiportId, accountingYear, isEmpty, previousCashFlowRiportId);
  }
  
  async updateCashFlowRiport() {
    await api.updateCashFlowRiport(this.cashFlowRiportHead, this.cashFlowRiportGroups, this.cashFlowRiportGroupItems, this.generalLedgerRiportRowItems);
  }
  
  async removeCashFlowRiport(cashFlowRiportId: number) { 
    await api.removeCashFlowRiport(cashFlowRiportId);
  }

  async loadCashFlowRiportChain() { 
    this.cashFlowRiportChain = await api.getCashFlowRiportChain(this.cashFlowRiportHead.reportHead);
  }

  //---------- Riport csoportok ----------//

  async loadCashFlowRiportGroups() {
    this.cashFlowRiportGroups = await api.getCashFlowRiportGroups(this.cashFlowRiportHead.id);
    this.profitGroups = this.cashFlowRiportGroups.filter(g => g.isProfit && g.name.toUpperCase() != "NYITÓ");    
    this.nonProfitGroups = this.cashFlowRiportGroups.filter(g => !g.isProfit);
    this.openAmountGroup = this.cashFlowRiportGroups.find(g => g.isProfit && g.name.toUpperCase() == "NYITÓ") ?? {...CashFlowRiportGroupDefaultProps};
  }

  //---------- Riport csoport tételei ----------//

  async loadCashFlowRiportGroupItems() {
    const groupIds: number[] = this.cashFlowRiportGroups.map(g => g.id);
    this.cashFlowRiportGroupItems = (await api.getCashFlowRiportGroupItems(groupIds));

    this.generatePivotForProfitGroupItems();
    this.generatePivotForNonProfitGroupItems();
    this.openAmountGroupItem = this.cashFlowRiportGroupItems.find(i => this.cashFlowRiportGroups.filter(g => g.isProfit && g.name.toUpperCase() == "NYITÓ").map((g) => g.id).includes(i.group)) ?? {...CashFlowRiportGroupItemsDefaultProps};
  }    

  //---------- Sablon sor tételek ---------//

  async loadGeneralLedgerRiportRowItems() {
    if(this.cashFlowRiportGroupItems) {
      const reportRows: number[] = this.cashFlowRiportGroupItems.map(i => i.reportRow);
      this.generalLedgerRiportRowItems = await api.getGeneralLedgerRiportRowItems(reportRows);
    }
  }

  //---------- Sablon tényadatok ---------//

  async loadGeneralLedgerRiportRowsTransAmounts() {
    
    this.generalLedgerRiportRowsTransAmounts = await api.getGeneralLedgerRiportRowsTransAmounts(this.cashFlowRiportHead.year, this.generalLedgerRiportRowItems);
    
    //Beleforgatom a lekérdezés értékeit a pivot tétel listába. Szűrök row szerint -> majd row on belül month szerint.
    this.generalLedgerRiportRowsTransAmounts.forEach(transAmount => {
      const nonProfitItem = this.nonProfitGroupItems?.find(i => i.reportRow == transAmount.reportRow);
      if (nonProfitItem) {
        const month = nonProfitItem.items.find(i => i.month == transAmount.month);
        if (month) {
          month.amount = transAmount.total * -1;
        }
      }
      const profitItem = this.profitGroupItems?.find(i => i.reportRow == transAmount.reportRow);
      if (profitItem) {
        const month = profitItem.items.find(i => i.month == transAmount.month);
        if (month) {            
          month.amount = transAmount.total * - 1;
        }
      }
      if (transAmount.reportRow == this.openAmountGroupItem.reportRow) {
        this.openAmountGroupItem.amount =  transAmount.total * -1;
      }
    });
  }

  //---------- PowerBI ---------//

  async loadBIReports() {
    this.biReports = await PowerBIApi.getAllByType(2);
  }

  //----------
  async loadAccountingYearsFromNow() { 
    this.accYears = await api.getAccountingYearsFromNow();
  }

  //Pivot referencia modell a kiadások tételeihez. Összevonás reportrow id szerint és a hónapok az alá gyűjtés
  GroupItemsToPivot(cashFlowGroupItems: CashFlowRiportGroupItem[]) {
    return Object.values(
      cashFlowGroupItems.reduce<Record<number, PivotGroupData>>((acc, item) => {
        if(!acc[item.reportRow]) {
          acc[item.reportRow] = {
            name: item.name,
            remark: item.remark,
            reportRow: item.reportRow,
            showDetails: false,
            items: [],
          };
        }
        acc[item.reportRow].items.push(item);
        return acc;
      }, {})
    );
  }

  generatePivotForProfitGroupItems() {
    const profitGroupItems = this.cashFlowRiportGroupItems.filter(i => this.cashFlowRiportGroups.filter(g => g.isProfit && g.name.toUpperCase() != "NYITÓ").map((g) => g.id).includes(i.group));
    this.profitGroupItems = this.GroupItemsToPivot(profitGroupItems);

    //FIXME: Így lehet csak frissítésre kényszeríteni a Vue-t?
    this.profitGroupItems[0].items[0].planAmount++;
    this.profitGroupItems[0].items[0].planAmount--;
  }

  generatePivotForNonProfitGroupItems() {
    const nonProfitGroupItems = this.cashFlowRiportGroupItems.filter(i => this.cashFlowRiportGroups.filter(g => !g.isProfit).map((g) => g.id).includes(i.group));
    this.nonProfitGroupItems = this.GroupItemsToPivot(nonProfitGroupItems);
    
    //FIXME: Így lehet csak frissítésre kényszeríteni a Vue-t?
    this.nonProfitGroupItems[0].items[0].planAmount++;
    this.nonProfitGroupItems[0].items[0].planAmount--;
  }

  loadUserPermissions() {
    this.userPermissions = {
      showMaskInfo: UserFeaturePermission.checkFeaturePermission({ modul: "CASHFLOWRIPORT", feature: "MASKINFOBUTTON" }),
      canOverrideOpenTrueAmount: UserFeaturePermission.checkFeaturePermission({ modul: "CASHFLOWRIPORT", feature: "OVERRIDEOPENAMOUNT" }),
      canEdit: UserFeaturePermission.checkFeaturePermission({ modul: "CASHFLOWRIPORT", feature: "EDITCASHFLOW" }),
    };
  }
}

const store = new CashFlowStore();
export default store;